/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DATE_FORMAT, regleEtatAnterieurConjoint } from '@/constants';

import validationInformationsNaissance from '../validationInformationsNaissance';

dayjs.extend(customParseFormat);

/**
 * Validation d'une étape « conjoint » d'un acte de mariage.
 *
 * L'état civil réutilise `validationInformationsNaissance` en mode « individu
 * principal » : nom, prénom usuel, sexe, date **et** lieu de naissance
 * obligatoires — les deux figurent sur l'acte papier.
 *
 * S'y ajoutent deux règles propres au mariage :
 *
 * - la date de naissance doit précéder la célébration ;
 * - la date de la situation antérieure, quand la situation en admet une et
 *   qu'elle est saisie, doit être valide et antérieure à la célébration.
 *
 * ⚠️ La situation antérieure elle-même reste OPTIONNELLE : 271 actes du fonds
 * repris n'en portent aucune, l'exiger empêcherait de les numériser.
 *
 * Les clés d'erreur sont celles des `name` de champs SANS le préfixe
 * `conjoint{n}.` — convention des validators, que les blocs de saisie lisent
 * telle quelle (cf. `validationStep2`).
 *
 * @param conjoint    Le conjoint (`data.conjoint{n}`), pas la racine de `data`
 * @param dateMariage Date de la célébration, au format de saisie
 */
const validationStepConjoint = (
  conjoint: Record<string, any>,
  dateMariage?: string,
) => {
  const errors: Record<string, string> = {
    ...validationInformationsNaissance(conjoint ?? {}),
  };

  const { anneeNaissance, moisNaissance, jourNaissance } = conjoint ?? {};
  const dateNaissance = dayjs(
    `${anneeNaissance}-${moisNaissance}-${jourNaissance}`,
    'YYYY-M-D',
  );
  const dateCelebration = dayjs(dateMariage, DATE_FORMAT, true);

  if (
    _.isNil(errors.dateNaissance) &&
    dateNaissance.isValid() &&
    dateCelebration.isValid() &&
    dateNaissance.isAfter(dateCelebration, 'day')
  ) {
    errors.dateNaissance =
      'La date de naissance doit être antérieure à la date du mariage';
  }

  /*
   * La date n'est contrôlée que si la situation en admet une : sur les autres
   * elle n'est même pas affichée, et une valeur résiduelle d'une situation
   * précédente ne doit pas bloquer l'étape — le back la purge.
   */
  const admetUneDate = !_.isNil(
    regleEtatAnterieurConjoint(conjoint?.etatMatrimonialAnterieureCode)
      .libelleDate,
  );

  if (admetUneDate && !_.isEmpty(conjoint?.dateEtatMatrimonial)) {
    const dateEtat = dayjs(conjoint.dateEtatMatrimonial, DATE_FORMAT, true);

    if (!dateEtat.isValid()) {
      errors.dateEtatMatrimonial = 'La date est invalide';
    } else if (
      dateCelebration.isValid() &&
      dateEtat.isAfter(dateCelebration, 'day')
    ) {
      errors.dateEtatMatrimonial =
        'La date de situation antérieure doit précéder le mariage';
    }
  }

  return _.pickBy(errors, (value) => !_.isEmpty(value));
};

export default validationStepConjoint;
